import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Button, Form, Spinner } from "react-bootstrap";
import AuthApi from "../../api/authApi";
import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import PhoneInput from "../../components/PhoneInput";
import { trimIfOnChangeField } from "../../utils/trimFormFields";
import { EMAIL_REGEX } from "../../utils/emailFormat";
import "../../assets/css/LsFormModern.css";

const CreateTicket: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const authApi = new AuthApi();
  const navigate = useNavigate();
  const [validated, setValidated] = useState<boolean>(false);
  const [captchaText, setCaptchaText] = useState<string>("");
  const [formData, setFormData] = useState<Record<string, any>>({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    subject: "",
    message: "",
    captchaAnswer: "",
    category: "",
  });
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<any[]>([]);
  const [loadData, setLoadData] = useState<boolean>(false);
  const [emailValid, setEmailValid] = useState<boolean>(true);

  const generateCaptcha = () => {
    setCaptchaText(Math.random().toString(36).substring(2, 8).toUpperCase());
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value: rawValue } = e.target;
    const value =
      typeof rawValue === "string"
        ? (trimIfOnChangeField(name, rawValue) as string)
        : rawValue;
    setFormData({ ...formData, [name]: value });

    if (name === "email") {
      setEmailValid(EMAIL_REGEX.test(value));
    }
  };

  // ✅ Handle file change (append + dedupe)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (!files.length) return;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    files.forEach((file) => {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");

      // ✅ Check type
      if (!isImage && !isVideo) {
        invalidFiles.push(`${file.name} ${t("invalid_type")}`);
        return;
      }

      // ✅ Check size
      if (isImage && file.size > 3 * 1024 * 1024) {
        invalidFiles.push(`${file.name}, ${t("three_mb_size")}`);
        return;
      }

      if (isVideo && file.size > 10 * 1024 * 1024) {
        invalidFiles.push(`${file.name}, ${t("ten_mb_size")}`);
        return;
      }

      validFiles.push(file);
    });

    // Show invalid files in toast
    if (invalidFiles.length > 0) {
      toast.error(`${t("files_are_invalid")}: ${invalidFiles.join(", ")}`);
    }

    if (validFiles.length === 0) return;

    // Add valid files, avoid duplicates
    setAttachments((prev) => {
      const combined = [...prev];
      validFiles.forEach((f) => {
        const exists = combined.some(
          (p) => p.name === f.name && p.size === f.size && p.type === f.type
        );
        if (!exists) combined.push(f);
      });
      return combined;
    });

    e.target.value = null;
  };

  // ✅ Generate / revoke preview URLs
  useEffect(() => {
    if (!attachments || attachments.length === 0) {
      setPreviewUrls([]);
      return;
    }

    const urls = attachments.map((file) => ({
      url: URL.createObjectURL(file),
      type: file.type,
    }));

    setPreviewUrls(urls);

    return () => {
      urls.forEach((u) => {
        try {
          URL.revokeObjectURL(u.url);
        } catch (err) { }
      });
    };
  }, [attachments]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidated(true);
    setLoadData(true);
    if (!formData.firstName || !formData.email || !formData.captchaAnswer) {
      toast.error(`${t("all_required_fields")}`);
      setLoadData(false);
      return;
    }

    try {
      const payload = new FormData();
      Object.keys(formData).forEach((key) => {
        payload.append(key, formData[key]);
      });
      payload.append("captcha", captchaText);
      payload.append("pageName", location?.state?.pageName || "");

      attachments.forEach((file) => {
        payload.append("attachments", file);
      });

      const res = await (authApi.createTicket as any)(payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.success) {
        setLoadData(false);
        if (res.msg === "Invalid captcha") {
          toast.success(`${t("invalid_captcha")}`);
        } else {
          toast.success(`${t("ticket_successfully")}`);
        }
        navigate("/dashboard");
      } else {
        setLoadData(false);
        toast.error(res.msg);
      }
    } catch (error) {
      setLoadData(false);
      console.log("error--->", error);
      toast.error(`${t("failed_to_create_ticket")}`);
    }
  };

  return (
    <div className="ls-form-page">
      <div className="ls-form-page-header">
        <h1 className="ls-form-page-title">{t("post_a_ticket")}</h1>
        <p className="ls-form-page-subtitle">
          Tell us what you need help with and we will follow up.
        </p>
      </div>
      <div className="ls-form-card ls-form">
        {loadData && (
          <div className="ls-form-spinner">
            <Spinner animation="border" />
          </div>
        )}
        {!loadData && (
          <Form
            noValidate
            validated={validated}
            onSubmit={handleSubmit}
            className="ls-form"
          >
            <div className="ls-form-section">
              <h3 className="ls-form-section-title">Ticket details</h3>
              <Form.Group>
                <Form.Label>
                  {t("ticket_category")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  required
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="locationDropdown"
                >
                  <option value="">{t("select_category")}</option>
                  <option value="Retreat Registration">
                    {t("retreat_registration")}
                  </option>
                  <option value="Retreat Financials">
                    {t("retreat_financials")}
                  </option>
                  <option value="Retreat Roster">
                    {t("retreat_roster")}
                  </option>
                  <option value="Retreat Closure">
                    {t("retreat_closure")}
                  </option>
                  <option value="Database Issues">
                    {t("database_issues")}
                  </option>
                  <option value="Retreat Reports">
                    {t("retreat_reports")}
                  </option>
                  <option value="Request to Feature">
                    {t("request_to_feature")}
                  </option>
                  <option value="How do I...">{t("how_do_i")}</option>
                  <option value="Other">{t("other")}</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {t("category_reqired")}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group>
                <Form.Label>
                  {`${t("subject")}`} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  name="subject"
                  placeholder={`${t("enter_ticket_subject")}`}
                  value={formData.subject}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>
                  {`${t("message")}`} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  required
                  name="message"
                  placeholder={`${t("describe_detail")}`}
                  value={formData.message}
                  onChange={handleChange}
                />
              </Form.Group>
            </div>

            <div className="ls-form-section">
              <h3 className="ls-form-section-title">Your information</h3>
              <div className="ls-form-grid">
                <Form.Group>
                  <Form.Label>
                    {`${t("first_name")}`}{" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="firstName"
                    placeholder={`${t("enter_your_name")}`}
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>{`${t("last_name")}`}</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="lastName"
                    placeholder={`${t("enter_your_last_name")}`}
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>
                    {`${t("email")}`} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    required
                    type="email"
                    name="email"
                    placeholder={`${t("enter_your_email")}`}
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!emailValid}
                  />
                  <Form.Control.Feedback type="invalid">
                    {t("invalid_email")}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>
                    {`${t("mobile")}`} (Ex- xxx-xxx-xxxx)
                  </Form.Label>
                  <PhoneInput
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="ls-form-section">
              <h3 className="ls-form-section-title">Attachments</h3>
              <Form.Group>
                <Form.Label>
                  {t("attachments")}{" "}
                  <small className="text-muted">{t("allowed")} </small>
                </Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileChange}
                />
              </Form.Group>

              {attachments.length > 0 && (
                <div>
                  <h6 className="fw-semibold mb-2">
                    {t("selected_files") || "Selected files"}
                  </h6>
                  <ul className="ls-form-file-list">
                    {attachments.map((file, idx) => {
                      const preview = previewUrls[idx]?.url;
                      const isImage = file.type.startsWith("image/");
                      const isVideo = file.type.startsWith("video/");

                      return (
                        <li
                          key={`${file.name}-${file.size}-${idx}`}
                          className="ls-form-file-item"
                        >
                          <div className="d-flex align-items-center">
                            {isImage && preview ? (
                              <img src={preview} alt={file.name} />
                            ) : isVideo && preview ? (
                              <video src={preview} muted playsInline />
                            ) : (
                              <span
                                style={{
                                  display: "inline-block",
                                  width: 48,
                                  height: 48,
                                  lineHeight: "48px",
                                  textAlign: "center",
                                  borderRadius: 8,
                                  background: "#e8edf3",
                                  marginRight: 12,
                                  fontSize: 12,
                                }}
                              >
                                {file.type.split("/")[1] || "file"}
                              </span>
                            )}
                            <div>
                              <div className="fw-medium">{file.name}</div>
                              <small className="text-muted">
                                {(file.size / 1024 / 1024).toFixed(2)} MB •{" "}
                                {file.type || "unknown"}
                              </small>
                            </div>
                          </div>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() =>
                              setAttachments((prev) =>
                                prev.filter((_, i) => i !== idx)
                              )
                            }
                          >
                            ✕
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <div className="ls-form-section">
              <h3 className="ls-form-section-title">Verification</h3>
              <div className="ls-form-captcha">
                <div className="ls-form-captcha-box">
                  <span>{captchaText}</span>
                  <RefreshCw
                    className="refresh-icon ms-2"
                    onClick={generateCaptcha}
                    size={18}
                  />
                </div>
                <Form.Group className="flex-grow-1">
                  <Form.Label>
                    {`${t("enter_captcha")}`}{" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="captchaAnswer"
                    placeholder={`${t("enter_captcha")}`}
                    value={formData.captchaAnswer}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="ls-form-footer">
              <Link to="/dashboard">
                <Button variant="secondary">{t("cancel")}</Button>
              </Link>
              <Button variant="primary" type="submit">
                {t("post_a_ticket")}
              </Button>
            </div>
          </Form>
        )}
      </div>
    </div>
  );
};

export default CreateTicket;
